import { motion } from "framer-motion";
import {
    TrendingUp,
    Users,
    Activity,
    Heart,
    Wallet,
    Eye,
    Target,
} from "lucide-react";
import { TiltCard } from "../effects/TiltCard";

const metrics = [
    {
        icon: TrendingUp,
        label: "Revenue Velocity",
        sub: "pipeline → cash, in real time",
        delta: "+24.6%",
        color: "text-emerald-300",
        ring: "from-emerald-400/30 to-emerald-400/0",
    },
    {
        icon: Users,
        label: "People Health",
        sub: "capacity, attrition, performance",
        delta: "92 / 100",
        color: "text-cyan-300",
        ring: "from-cyan-400/30 to-cyan-400/0",
    },
    {
        icon: Activity,
        label: "Operational Throughput",
        sub: "tasks shipped vs planned",
        delta: "118%",
        color: "text-amber-glow",
        ring: "from-amber-400/30 to-amber-400/0",
    },
    {
        icon: Heart,
        label: "Customer Pulse",
        sub: "NPS, churn signals, support",
        delta: "+8 pts",
        color: "text-rose-300",
        ring: "from-rose-400/30 to-rose-400/0",
    },
    {
        icon: Wallet,
        label: "Cash Runway",
        sub: "burn, MRR, projections",
        delta: "31 mo",
        color: "text-violet-300",
        ring: "from-violet-400/30 to-violet-400/0",
    },
    {
        icon: Target,
        label: "Strategic Focus",
        sub: "OKRs, initiatives, priorities",
        delta: "87%",
        color: "text-blue-300",
        ring: "from-blue-400/30 to-blue-400/0",
    },
];

export const LeadershipView = () => {
    return (
        <section
            id="leadership"
            className="relative py-24 sm:py-32 overflow-hidden"
            data-testid="leadership-view-section"
        >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <div className="absolute inset-0 radial-glow opacity-40 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 glass rounded-full px-3 py-1 mb-6">
                            <Eye className="w-3.5 h-3.5 text-cyan-300" />
                            <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-zinc-300">
                                Leadership View
                            </span>
                        </div>
                        <p className="text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4 font-code">
                            // built for founders & operators
                        </p>
                        <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.05]">
                            Six numbers that tell you
                            <br />
                            <span className="gradient-text-amber">
                                exactly how the company is doing.
                            </span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-zinc-400 text-base sm:text-lg leading-relaxed font-light">
                        No more 30‑tab Monday reviews. ApexBrain surfaces the six
                        signals every founder needs - refreshed automatically, traceable
                        to the underlying work, and explainable on demand.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
                    {metrics.map((m, i) => (
                        <motion.div
                            key={m.label}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.5, delay: i * 0.06 }}
                            data-testid={`leadership-metric-${i}`}
                        >
                            <TiltCard className="relative h-full rounded-2xl border border-white/[0.06] bg-obsidian-200/70 backdrop-blur-sm p-6 overflow-hidden hover:border-white/15 transition-colors">
                                <div
                                    className={`absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${m.ring} blur-3xl pointer-events-none`}
                                />
                                <div className="relative flex items-start justify-between">
                                    <div className="w-10 h-10 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
                                        <m.icon className={`w-4 h-4 ${m.color}`} />
                                    </div>
                                    <span
                                        className={`font-code text-[11px] font-semibold tabular-nums ${m.color}`}
                                    >
                                        {m.delta}
                                    </span>
                                </div>
                                <div className="relative mt-5">
                                    <div className="font-outfit text-lg font-semibold text-white">
                                        {m.label}
                                    </div>
                                    <div className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
                                        {m.sub}
                                    </div>
                                </div>
                                <div className="relative mt-6 flex items-center justify-between text-[10px] font-code text-zinc-600">
                                    <span>signal_{String(i + 1).padStart(2, "0")}</span>
                                    <span className="inline-flex items-center gap-1">
                                        <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse-glow" />
                                        live
                                    </span>
                                </div>
                            </TiltCard>
                        </motion.div>
                    ))}

                
                </div>
            </div>
        </section>
    );
};
